import { IResource } from "@gettactic/api";
import { Shape } from "@gettactic/map-builder/src/types";
import { getSpotLabelsByResourceId } from "@gettactic/map-builder/src/utils/spots";
import type { QueryClient } from "@tanstack/react-query";

interface ApplySavedFloorMapDataParams {
  queryClient: QueryClient;
  floorId: string;
  /** The canonical `map_data` returned by `resourceMapPost`. */
  savedMapData: string;
  /** The shapes that were actually sent in the save request — used to read
   *  back each resource's edited label/name. */
  sentShapes: Shape[];
  /**
   * Whether to also invalidate the full resources list. Defaults to true
   * (skipped) because the full map editor keeps saving a draft in place —
   * refetching would replace that draft's `map_data` with whatever is
   * currently *active* on the server. A quick single-resource create has no
   * draft to protect, and needs the new resource to show up immediately, so
   * it should pass `false`.
   */
  skipResourcesListRefetch?: boolean;
}

/**
 * Mirrors a floor map save into the TanStack Query cache: stamps the
 * server-canonical `map_data` onto the floor and the edited labels/names
 * onto their resources, then invalidates the queries that reflect the
 * change elsewhere in the app. Shared by every map editor that saves via
 * `resourceMapPost` so they stay in sync.
 */
export const applySavedFloorMapData = ({
  queryClient,
  floorId,
  savedMapData,
  sentShapes,
  skipResourcesListRefetch = true
}: ApplySavedFloorMapDataParams): void => {
  const savedSpotLabels = getSpotLabelsByResourceId(sentShapes);

  // Update the floor's map_data directly in the resources cache so that
  // when a refetch runs, it already has the correct data. This avoids a
  // race where the server returns stale data briefly.
  queryClient.setQueriesData<{ elements: IResource[] }>(
    { queryKey: ["organizations.resourcesAll"] },
    (old) => {
      if (!old?.elements) return old;
      return {
        ...old,
        elements: old.elements.map((el) => {
          if (el.id === floorId) {
            return { ...el, map_data: savedMapData };
          }
          const savedSpot = savedSpotLabels.get(el.id);
          return savedSpot
            ? {
                ...el,
                label: savedSpot.label,
                name: savedSpot.name ?? el.name
              }
            : el;
        })
      };
    }
  );

  // Invalidate side queries that reflect external state.
  queryClient.invalidateQueries({ queryKey: ["organizations.offices"] });
  queryClient.invalidateQueries({ queryKey: ["schedules"] });
  queryClient.invalidateQueries({ queryKey: ["occupied"] });

  if (!skipResourcesListRefetch) {
    queryClient.invalidateQueries({
      queryKey: ["organizations.resourcesAll"]
    });
  }
};
